/* global Sys, Core, Services */
Sys.ns("Core");

/**
 * The Settings Manager controller.
 *
 * @class Core.SettingsManagerController
 * @extends Core.Controller
 */
Core.SettingsManagerController = {

    constructor: function() {
        Core.SettingsManagerController.superclass.constructor.apply(this, arguments);
    },

    /**
     * @protected
     * @inheritdoc
     */
    setupEvents: function() {
        var me = this;

        me.on({
            "request:settingsManager.storeData": me.storeSetting,
            "request:settingsManager.lockSetting": me.lockSetting,
            "request:settingsManager.unlockSetting": me.unlockSetting,
            "request:settingsManager.disableSetting": me.disableSetting,
            "request:settingsManager.enableSetting": me.enableSetting
        });
    },

    /**
     * @protected
     * @returns {void}
     */
    registerTriggers: function() {
        Services.trigger.registerTrigger("notify:settingsManager.settingChanged", this, "When the settings are changed");
    },

    /**
     * Check if a setting is settable, i.e. neither disabled or locked.
     *
     * @param {string} setting The setting.
     * @returns {boolean} If setting is settable.
     */
    isSettable: function(setting) {
        var me = this;

        return !me.model.isLocked(setting) && !me.model.isDisabled(setting);
    },

    /**
     * Stores a value for a given setting.
     *
     * It fires an event to notify all modules that the setting has been changed.
     *
     * @param {string} setting The setting to change.
     * @param {*} value The setting value.
     * @param {boolean} [useLocalStorage=false] Flag indicating if the new value should be stored in local storage.
     * @param {boolean} [silent=false] Flag indicating if an event shall be fired.
     * @returns {void}
     */
    storeSetting: function(setting, value, useLocalStorage, silent) {
        var me = this,
            settings;

        if (!me.model.isLocked(setting) && !me.model.isDisabled(setting)) {
            settings = me.model.readData("settings");

            if (settings[setting] !== value) {
                settings[setting] = value;
                me.model.storeData("settings", settings);
                if (useLocalStorage) {
                    Services.localStorageManager.storeData(setting, typeof value === "string" ? value : JSON.stringify(value));
                }

                if (!silent) {
                    me.fireEvent("notify:settingsManager.settingChanged", setting, value);
                }
            }

            /* DEBUG_START */
            else {
                console.info("Request to set the setting '" + setting + "' to the same value it already has.");
            }

            /* DEBUG_END */
        }

        /* DEBUG_START */
        else {
            console.warn("Request to set the setting '" + setting + "' when it is already locked/disabled!");
        }

        /* DEBUG_END */
    },

    /**
     * Gets a value for a given setting.
     *
     * @param {string} setting The setting.
     * @returns {*} The value.
     */
    getSetting: function(setting) {
        return this.model.readData("settings")[setting];
    },

    /**
     * Locks a setting from being edited.
     *
     * @param {string} setting The setting that should be locked.
     * @param {Object} who The instance that wants to lock the setting.
     * @returns {void}
     */
    lockSetting: function(setting, who) {
        var lockedSettings = this.model.readData("lockedSettings");

        if (!Sys.isArray(lockedSettings[setting])) {
            lockedSettings[setting] = [];
        }

        if (!Sys.contains(lockedSettings[setting], who)) {
            lockedSettings[setting].push(who);

            this.model.storeData("lockedSettings", lockedSettings);

            if (lockedSettings[setting].length === 1) {
                this.fireEvent("notify:settingsManager.settingLocked", setting);
            }
        }

        /* DEBUG_START*/
        else {
            console.warn(who + " is trying to lock the setting '" + setting + "' when it is already locked by that object!");
        }

        /* DEBUG_END*/
    },

    /**
     * Unlocks a setting so that it can be edited.
     *
     * The setting will remain locked until all instances that have requested the lock have requested an unlock of it.
     *
     * @param {string} setting The setting that should be unlocked.
     * @param {Object} who The instance that wants to unlock the setting.
     * @returns {void}
     */
    unlockSetting: function(setting, who) {
        var lockedSettings = this.model.readData("lockedSettings");

        if (Sys.isArray(lockedSettings[setting]) && Sys.contains(lockedSettings[setting], who)) {
            lockedSettings[setting].splice(lockedSettings[setting].indexOf(who), 1);
            this.model.storeData("lockedSettings", lockedSettings);
            if (lockedSettings[setting].length === 0) {
                this.fireEvent("notify:settingsManager.settingUnlocked", setting);
            }
        }

        /* DEBUG_START*/
        else {
            console.warn(who + " is trying to unlock the setting '" + setting + "' when it is not locked by that object!");
        }

        /* DEBUG_END*/
    },

    /**
     * Disables a setting.
     *
     * @param {string} setting The setting.
     * @returns {void}
     */
    disableSetting: function(setting) {
        var me = this;

        me.model.readData("disabledSettings")[setting] = true;
        me.fireEvent("notify:settingsManager.settingDisabled", setting);
    },

    /**
     * Enables a setting.
     *
     * @param {string} setting The setting.
     * @returns {void}
     */
    enableSetting: function(setting) {
        var me = this;

        me.model.readData("disabledSettings")[setting] = false;
        me.fireEvent("notify:settingsManager.settingEnabled", setting);
    }
};

Core.SettingsManagerController = Sys.extend(Core.Controller, Core.SettingsManagerController, "Core.SettingsManagerController");
